"use client"

import { useState } from "react"
import { Save, FolderOpen } from "@/lib/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { useTagSetStore } from "@/lib/tag-set-store"
import { useTranslations } from "next-intl"

interface SavePresetModalProps {
  isOpen: boolean
  onClose: () => void
  currentTags: string[]
}

export function SavePresetModal({ isOpen, onClose, currentTags }: SavePresetModalProps) {
  const t = useTranslations("tagPresetModals")
  const [presetName, setPresetName] = useState("")
  const { saveTagPreset } = useTagSetStore()

  const handleSave = () => {
    if (presetName.trim()) {
      saveTagPreset(presetName.trim(), currentTags)
      setPresetName("")
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm bg-background/95 backdrop-blur-md border border-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 justify-center py-1">
            <Save className="h-5 w-5" />
            {t("saveTagPreset")}
          </DialogTitle>
          <DialogDescription>{t("saveTheCurrentTagsAsAReusablePreset")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            placeholder={t("presetNameEGFavoriteTags")}
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
            className="bg-secondary/30 border-white/10"
          />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={onClose}>
              {t("cancel")}
            </Button>
            <Button
              onClick={handleSave}
              disabled={!presetName.trim()}
              className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300"
            >
              <Save className="h-4 w-4 mr-2" />
              {t("savePreset")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface LoadPresetModalProps {
  isOpen: boolean
  onClose: () => void
  onLoadPreset: (tags: string[], name: string) => void
}

export function LoadPresetModal({ isOpen, onClose, onLoadPreset }: LoadPresetModalProps) {
  const t = useTranslations("tagPresetModals")
  const { tagPresets, deleteTagPreset } = useTagSetStore()

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm bg-background/95 backdrop-blur-md border border-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-center gap-2 my-0 py-1">
            <FolderOpen className="h-5 w-5" />
            {t("loadTagPreset")}
          </DialogTitle>
          <DialogDescription>{t("selectAPresetToLoadItsTags")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {tagPresets.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">{t("noPresetsSavedYetCreateOneFirst")}</p>
          ) : (
            tagPresets.map((preset) => (
              <div
                key={preset.id}
                className="flex items-center justify-between p-2 rounded border border-white/10 bg-secondary/20 hover:bg-secondary/40 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{preset.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("tags", { count: preset.tags.length })}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      onLoadPreset(preset.tags, preset.name)
                      onClose()
                    }}
                    className="h-6 px-2 text-xs hover:bg-cyan-500/20"
                  >
                    {t("load")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteTagPreset(preset.id)}
                    className="h-6 px-2 text-xs hover:bg-red-500/20 text-red-300"
                  >
                    {t("delete")}
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
